import React, { useMemo } from 'react';
import { useValueChange } from '@leverege/ui-hooks';
import { ModelEditorFactory, ModelTypeSelector } from '@leverege/ui-plugin';
import { Content, Label, Pane, PropertyGrid, TabPane, TextInput, Checkbox } from '@leverege/ui-elements';
import { Attributes } from '@leverege/ui-attributes';
import { Plugins } from '@leverege/plugin';
import I18N from '@leverege/i18n';
import { R } from '@leverege/value-resolver';
import AggsViewModel from "../models/AggsViewModel.js";
import DataViewers from "../../DataViewers.js";
import S from './AggsViewEditor.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t
} = I18N.ns('molten.dataViewers.aggregations.AggsViewEditor');
export default function AggsViewEditor(props) {
  const {
    value,
    objectType,
    delegate,
    eventData,
    onChange,
    relationship
  } = props;
  const onModelChange = useValueChange(AggsViewModel, props);
  let subEditors = [];
  if (Array.isArray(value?.dataViewers)) {
    const hasBreakpoints = value.dataViewers.length > 1;
    subEditors = value.dataViewers.map((dv, idx) => {
      if (dv.settings == null) {
        return null;
      }
      const dataViewer = DataViewers.get(dv);
      const eProps = {
        value: dv.settings,
        onChange: evt => {
          const nValue = AggsViewModel.setDataViewerSettings(value, evt.data, evt.value);
          onChange({
            value: nValue,
            data: eventData
          });
        },
        eventData: idx,
        objectType: 'aggResult',
        relationship,
        delegate
      };
      const editor = ModelEditorFactory.create(dv.settings, eProps);
      if (editor == null) {
        return null;
      }
      const brk = hasBreakpoints && idx > 0 && value.breakpoints[idx - 1];
      const tabName = R(dataViewer?.editorName || dataViewer?.name || t('editor'));
      return /*#__PURE__*/_jsxs(TabPane.Tab, {
        tabName: typeof tabName === 'function' ? tabName() : tabName,
        tabId: dv.id || idx,
        children: [hasBreakpoints && idx > 0 && /*#__PURE__*/_jsxs(Content, {
          children: [/*#__PURE__*/_jsx(Content.Area, {
            style: {
              overflow: 'hidden'
            },
            children: editor
          }), /*#__PURE__*/_jsx(Content.Footer, {
            children: /*#__PURE__*/_jsx(PropertyGrid, {
              inline: true,
              children: /*#__PURE__*/_jsx(PropertyGrid.Item, {
                newRow: true,
                label: t('widthGreater', {
                  tabName
                }),
                children: /*#__PURE__*/_jsx(TextInput, {
                  value: typeof brk === 'number' ? `${brk}` : brk,
                  eventData: idx - 1,
                  onChange: evt => {
                    let v = parseInt(evt.value);
                    // eslint-disable-next-line no-restricted-globals
                    if (isNaN(v)) {
                      v = evt.value;
                    }
                    const nValue = AggsViewModel.setBreakpoint(value, evt.data, v);
                    onChange({
                      value: nValue,
                      data: eventData
                    });
                  }
                })
              })
            })
          })]
        }), !(hasBreakpoints && idx > 0) && editor]
      }, dv.id || idx);
    });
  }
  const attributes = useMemo(() => {
    return Attributes.getAttributesFor(objectType);
  }, [objectType]);
  const typeOptions = value.options?.excludes?.length ? Plugins.get('AggModel').filter(type => !value.options.excludes.includes(type.name)) : Plugins.get('AggModel');
  return /*#__PURE__*/_jsxs(TabPane, {
    className: S.editor,
    children: [subEditors, /*#__PURE__*/_jsx(TabPane.Tab, {
      tabName: t('aggregation'),
      tabId: "aggregation",
      children: /*#__PURE__*/_jsx(Pane, {
        style: {
          '--propertyGrid-numColumns': 2
        },
        children: /*#__PURE__*/_jsx(PropertyGrid, {
          children: ModelTypeSelector.createSelectorAndEditor({
            values: typeOptions,
            value: AggsViewModel.getAggregation(value),
            eventData: 'setAggregation',
            onChange: onModelChange,
            allowNone: false,
            isPropertyGrid: true,
            modelKey: 'model',
            propertyGridOpts: {
              fill: true,
              preLabel: /*#__PURE__*/_jsx(Label, {
                children: t('aggregationType')
              })
            },
            editorProps: {
              objectType,
              delegate,
              attributes,
              relationship
            }
          })
        })
      })
    }), /*#__PURE__*/_jsx(TabPane.Tab, {
      tabName: t('optionsTab'),
      tabId: "options",
      children: /*#__PURE__*/_jsx(PropertyGrid, {
        children: /*#__PURE__*/_jsx(PropertyGrid.Item, {
          fillCell: true,
          children: /*#__PURE__*/_jsx(Checkbox, {
            label: t('allowExpandedView'),
            eventData: "setAllowExpandedView",
            value: AggsViewModel.getAllowExpandedView(value),
            onChange: evt => {
              const nValue = AggsViewModel.setAllowExpandedView(value, evt.value);
              onChange({
                eventData: evt.data,
                value: nValue
              });
            }
          })
        })
      })
    })]
  });
}